import { ApiProblem, type Api, type Schema } from '@cdn/api';
import { Observable, Signal } from '@cdn/state';
import type { Departures } from './departures';
import type { Destination } from './destination';
import type { Drafts } from './drafts';
import { Ending } from './ending';
import { Outage } from './outage';
import { Remembered } from './remembered';
import type { Identity, SignInRequest } from './port';

interface Specs {
	api: Api;
	identity: Identity;
	destination: Destination;
	drafts: Drafts;
	/** Whether the sign-in screen may leave for the authority by itself; absent, it always asks */
	departures?: Departures;
	/** Remembers the last organization so the home path can return to it; it holds an identifier only */
	storage?: Pick<Storage, 'getItem' | 'setItem'>;
	/** Sends the browser away (`leave`); tests observe it instead */
	navigate?: (url: string) => void;
	clock?: () => number;
	/** Milliseconds after which a session check the service did not answer is tried again (D132); tests shorten them */
	again?: readonly number[];
}

/**
 * The signed-in account and its organization scope.
 *
 * `restoring` until the first answer, `anonymous` without a credential and `active` with one. A session that ends
 * while the person works does not change it: the page's `Session` of `@beyond-js/ui` (`Renewal`) renews it
 * without being seen or asks the person once, over the screens, which stay in place with what was typed
 * (the family rule, Beyond Suite's `docs/family/session-renewal.md`). `unreachable` when the session could not be
 * checked because the service did not answer: an outage is not a signed-out person, so nothing is cleared, the
 * check is tried again by itself (`outage`, D132) and `retry()` asks at once; the screens open once it answers.
 *
 * Organizations, roles, capabilities and plan come only from the CDN session document. Selecting another
 * organization emits `scope`, on which every holder of scoped data and realtime subscriptions lets go of them.
 */
export class Session extends Observable {
	/** Milliseconds after a reading before a return to the tab reads the session again */
	static readonly REVISIT = 30000;

	#api: Api;
	#remembered: Remembered;
	#navigate: (url: string) => void;
	#clock: () => number;
	#read = 0; // when the session document was last read
	#releases: Array<() => void> = [];

	/** Why the last session ended and which organizations the person stopped belonging to (D26) */
	readonly ending = new Ending();

	#identity: Identity;
	get identity() {
		return this.#identity;
	}

	#destination: Destination;
	get destination() {
		return this.#destination;
	}

	#drafts: Drafts;
	get drafts() {
		return this.#drafts;
	}

	#departures: Departures | undefined;
	get departures() {
		return this.#departures;
	}

	#status: 'restoring' | 'anonymous' | 'active' | 'unreachable' = 'restoring';

	/** While `unreachable`: the check tried again by itself, and whether the service refuses this page's address */
	readonly outage: Outage;
	get status() {
		return this.#status;
	}

	#document: Schema.Session | undefined;
	get user() {
		return this.#document?.user;
	}

	get organizations(): Schema.Membership[] {
		return this.#document?.organizations ?? [];
	}

	#selected: string | undefined;
	/**
	 * The membership of the selected organization
	 */
	get organization(): Schema.Membership | undefined {
		return this.organizations.find(membership => membership.organization === this.#selected);
	}

	/**
	 * The organization to open from the home path: the last one used, otherwise the first
	 */
	get preferred(): Schema.Membership | undefined {
		const kept = this.#remembered.organization;
		return this.organizations.find(membership => membership.organization === kept) ?? this.organizations[0];
	}

	get plan(): Schema.PlanSummary | undefined {
		return this.organization?.plan;
	}

	/** Emits the organization identifier whenever the scope changes, and undefined when the account leaves */
	readonly scope = new Signal<string | undefined>();

	/**
	 * Emits the person's language and appearance as Beyond Accounts holds them, each time the application learns
	 * who is signed in (decision D07); nothing when the service did not answer them
	 */
	readonly arrival = new Signal<Schema.Preferences>();

	constructor({ api, identity, destination, drafts, departures, storage, navigate, clock, again }: Specs) {
		super();
		this.#api = api;
		this.#identity = identity;
		this.#destination = destination;
		this.#drafts = drafts;
		this.#departures = departures;
		this.#remembered = new Remembered(storage);
		this.#navigate = navigate ?? (url => globalThis.location.assign(url));
		this.#clock = clock ?? (() => Date.now());
		this.outage = new Outage({ api, check: () => this.restore(), delays: again, clock: this.#clock });

		identity.rotated && this.#releases.push(identity.rotated(token => this.#rotate(token)));
	}

	/**
	 * @param capability A CDN capability such as `release.promote`, as the session document lists them
	 */
	can(capability: string): boolean {
		return this.organization?.capabilities.includes(capability) ?? false;
	}

	member(organization: string): boolean {
		return this.organizations.some(membership => membership.organization === organization);
	}

	async restore() {
		try {
			const token = await this.#identity.restore();
			if (!token) this.ending.record(this.#identity.ended);
			token ? await this.#open(token) : this.#close();
		} catch (problem) {
			if (!ApiProblem.of(problem).outage) return this.#close();
			await this.outage.failed(problem);
			this.#status = 'unreachable';
			this.changed();
		}
	}

	/**
	 * Checks the session again after the service did not answer, keeping the outage in view while it does
	 */
	async retry() {
		if (this.#status === 'unreachable') await this.outage.check();
	}

	/**
	 * @returns false when the browser is leaving for an identity provider
	 * @throws {ApiProblem}
	 */
	async signin(request: SignInRequest): Promise<boolean> {
		const token = await this.#identity.signin(request);
		if (!token) return false;
		await this.#open(token);
		return true;
	}

	/**
	 * Signing out is a choice: the sign-in screen that follows asks before leaving for the authority again,
	 * which would otherwise sign the person straight back in
	 */
	async signout() {
		await this.end();
		this.#close();
	}

	/** Forgets the session here once it was ended: the sign-in screen follows */
	close() {
		this.#close();
	}

	/** Ends CDN's own session within the adapter's bound; the sign-in screen then asks before leaving again */
	async end() {
		this.#departures?.hold();
		await this.#identity.signout().catch(() => undefined);
	}

	/**
	 * "Sign out of Beyond" (Q09): ends CDN's session, then goes to Accounts' `/leave` (`address`), which ends the
	 * Beyond session and every session derived from it. Without an address it signs out of CDN only. Unsent form
	 * values stay as drafts on this device, so nothing is lost and nothing is asked.
	 */
	async leave(address: string | undefined) {
		await this.end();
		if (!address) return this.#close();
		this.#navigate(address);
	}

	/** Reads the session again on a return to the tab (at most once per `REVISIT`), so a lost membership is said (D26) */
	async revisit() {
		if (this.#status !== 'active' || this.#clock() - this.#read < Session.REVISIT) return;
		await this.refresh().catch(() => undefined);
	}

	/**
	 * Selects the organization of the current view. Changing it resets everything scoped to the previous one.
	 */
	select(organization: string | undefined) {
		if (organization === this.#selected) return;
		if (organization !== undefined && !this.member(organization)) return;
		this.#selected = organization;
		this.#drafts.own(this.user?.id, organization);
		this.#remembered.organization = organization;
		this.scope.emit(organization);
		this.changed();
	}

	/**
	 * Reloads organizations, roles and plan, for example after the plan changed
	 */
	async refresh() {
		if (this.#status !== 'active') return;
		this.#read = this.#clock();
		const document = await this.#api.call('session.read');
		this.#adopt(document);
		this.changed();
	}

	destroy() {
		this.outage.stop();
		for (const release of this.#releases) release();
	}

	async #open(token: string) {
		this.#api.credentials.set(token);
		let document: Schema.Session;
		try {
			document = await this.#api.call('session.read');
		} catch (problem) {
			this.#api.credentials.clear();
			// An outage while restoring leaves nothing closed: the person is not signed out, the service is away
			if ((this.#status === 'restoring' || this.#status === 'unreachable') && ApiProblem.of(problem).outage) throw problem;
			if (ApiProblem.of(problem).is('UNAUTHENTICATED')) await this.#identity.signout().catch(() => undefined);
			this.#close();
			throw problem;
		}

		// Another account on this tab must not inherit the scope or the screens of the previous one
		const other = this.#document !== undefined && this.#document.user.id !== document.user.id;
		other && this.ending.forget();
		this.#adopt(document);
		this.#read = this.#clock();
		this.ending.clear();
		this.#status = 'active';
		this.outage.stop();
		if (other) {
			this.#selected = undefined;
			this.scope.emit(undefined);
		}
		this.#drafts.own(document.user.id, this.#selected);
		document.preferences && this.arrival.emit(document.preferences);
		this.changed();
	}

	#adopt(document: Schema.Session) {
		const same = this.#document?.user.id === document.user.id;
		same && this.ending.compare(this.#document?.organizations ?? [], document.organizations);
		this.#document = document;
		if (this.#selected && !this.member(this.#selected)) {
			this.#selected = undefined;
			this.scope.emit(undefined);
		}
	}

	// A credential the provider ended is dropped: the next request is refused, and the page's `Session` answers it
	#rotate(token: string | undefined) {
		token ? this.#api.credentials.set(token) : this.#api.credentials.clear();
	}

	#close() {
		const had = this.#document !== undefined;
		this.#api.credentials.clear();
		this.#document = undefined;
		this.#selected = undefined;
		this.#status = 'anonymous';
		this.outage.stop();
		this.#drafts.own(undefined, undefined);
		had && this.scope.emit(undefined);
		this.changed();
	}
}
